
import { Sparkles, RefreshCw, FlaskConical, PackageCheck, Archive, RotateCcw, AlertCircle } from "lucide-react";
import type { BatchDetailResponse } from "../../gen/models";

interface BatchActionsBarProps {
  batch: BatchDetailResponse;
  actionLoading: boolean;
  onProcessBatch: () => void;
  onOpenSimulator: () => void;
  onOpenRejectModal: () => void;
  onOpenPromoteModal: () => void;
  onOpenArchiveModal: () => void;
  onOpenReopenModal?: () => void;
}

export function BatchActionsBar({
  batch,
  actionLoading,
  onProcessBatch,
  onOpenSimulator,
  onOpenRejectModal,
  onOpenPromoteModal,
  onOpenArchiveModal,
  onOpenReopenModal,
}: BatchActionsBarProps) {
  return (
    <div className="px-6 py-3 bg-stone-50 dark:bg-stone-950/80 border-b border-stone-200 dark:border-stone-800 flex flex-wrap items-center justify-between gap-3 text-xs">
      <div className="flex items-center gap-4 text-stone-500 dark:text-stone-400 flex-wrap">
        <span>
          Conversations:{" "}
          <strong className="text-stone-800 dark:text-stone-200">
            {batch.threads_count ??
              batch.threads?.length ??
              new Set([
                ...(batch.comments || []).map((c: any) => c.thread_id).filter(Boolean),
                ...(batch.evaluations || []).map((e: any) => e.thread_id).filter(Boolean),
              ]).size}
          </strong>
        </span>
        <span>•</span>
        <span>
          Comments: <strong className="text-stone-800 dark:text-stone-200">{batch.comments?.length || 0}</strong>
        </span>
        <span>•</span>
        <span>
          Evaluations: <strong className="text-stone-800 dark:text-stone-200">{batch.evaluations?.length || 0}</strong>
        </span>
        <span>•</span>
        <span>
          {batch.status === "completed" ? "Created Rules" : "Draft Rules"}:{" "}
          <strong className="text-stone-800 dark:text-stone-200">{batch.draft_rules?.length || 0}</strong>
        </span>
        <span>•</span>
        <span>
          {batch.status === "completed" ? "Created Knowledge" : "Draft Knowledge"}:{" "}
          <strong className="text-stone-800 dark:text-stone-200">{batch.draft_knowledge_items?.length || 0}</strong>
        </span>
      </div>

      <div className="flex items-center gap-2">
        {batch.status === "collecting" && (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onOpenArchiveModal}
              disabled={actionLoading}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-stone-200 dark:border-stone-800 text-stone-600 dark:text-stone-400 font-medium hover:bg-stone-100 dark:hover:bg-stone-900 transition disabled:opacity-50"
            >
              <Archive className="w-3.5 h-3.5" />
              <span>Archive</span>
            </button>
            <button
              type="button"
              onClick={onProcessBatch}
              disabled={actionLoading || ((batch.comments?.length || 0) === 0 && (batch.evaluations?.length || 0) === 0)}
              className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-stone-900 dark:bg-stone-100 text-white dark:text-stone-900 font-semibold shadow-2xs hover:bg-stone-800 dark:hover:bg-white transition disabled:opacity-50"
            >
              {actionLoading ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
              <span>Process Batch</span>
            </button>
          </div>
        )}

        {batch.status === "processing" && (
          <div className="flex items-center gap-2 text-indigo-600 dark:text-indigo-400 font-medium">
            <RefreshCw className="w-3.5 h-3.5 animate-spin" />
            <span>Synthesizing changesets (polling every 3s)...</span>
          </div>
        )}

        {batch.status === "failed_processing" && (
          <div className="flex items-center gap-2">
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-rose-50 dark:bg-rose-950/60 text-rose-700 dark:text-rose-300 border border-rose-200 dark:border-rose-800 font-medium">
              <AlertCircle className="w-3.5 h-3.5 text-rose-500" />
              <span>Processing Failed</span>
            </div>
            {onOpenReopenModal && (
              <button
                type="button"
                onClick={onOpenReopenModal}
                disabled={actionLoading}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-stone-200 dark:border-stone-800 text-stone-700 dark:text-stone-300 font-medium hover:bg-stone-100 dark:hover:bg-stone-900 transition disabled:opacity-50 cursor-pointer"
                title="Reset batch to collecting mode to edit items or retry"
              >
                <RotateCcw className="w-3.5 h-3.5" />
                <span>Reset to Collecting</span>
              </button>
            )}
            <button
              type="button"
              onClick={onProcessBatch}
              disabled={actionLoading}
              className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg bg-stone-900 dark:bg-stone-100 text-white dark:text-stone-900 font-semibold shadow-2xs hover:bg-stone-800 dark:hover:bg-white transition disabled:opacity-50 cursor-pointer"
            >
              {actionLoading ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
              <span>Retry Processing</span>
            </button>
          </div>
        )}

        {((batch.draft_rules?.length || 0) > 0 || (batch.draft_knowledge_items?.length || 0) > 0) && (
          <button
            type="button"
            onClick={onOpenSimulator}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-indigo-200 dark:border-indigo-800 text-indigo-700 dark:text-indigo-300 font-semibold hover:bg-indigo-50 dark:hover:bg-indigo-950/50 transition shadow-2xs"
          >
            <FlaskConical className="w-3.5 h-3.5" />
            <span>Simulate Batch</span>
          </button>
        )}

        {batch.status === "review_ready" && (
          <div className="flex items-center gap-2">
            {onOpenReopenModal && (
              <button
                type="button"
                onClick={onOpenReopenModal}
                disabled={actionLoading}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border font-medium transition cursor-pointer ${
                  batch.is_stale
                    ? "border-rose-300 dark:border-rose-700 bg-rose-50 dark:bg-rose-950/60 text-rose-700 dark:text-rose-300 hover:bg-rose-100 dark:hover:bg-rose-900/60 font-semibold shadow-2xs"
                    : "border-amber-200 dark:border-amber-800 text-amber-700 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-950/50"
                }`}
                title={
                  batch.is_stale
                    ? "Reset batch to collecting mode to discard outdated drafts and re-run processing"
                    : "Reset batch to collecting mode"
                }
              >
                <RotateCcw className="w-3.5 h-3.5" />
                <span>Reset to Collecting</span>
              </button>
            )}
            <button
              type="button"
              onClick={onOpenRejectModal}
              disabled={actionLoading}
              className="px-3 py-1.5 rounded-lg border border-rose-200 dark:border-rose-800 text-rose-700 dark:text-rose-300 font-medium hover:bg-rose-50 dark:hover:bg-rose-950/50 transition cursor-pointer"
            >
              Reject Batch
            </button>
            <button
              type="button"
              onClick={onOpenPromoteModal}
              disabled={actionLoading || Boolean(batch.is_stale)}
              title={
                batch.is_stale
                  ? `Cannot promote: ${batch.stale_reason || "One or more proposed changes has already been altered by another version"}`
                  : "Approve & Promote Batch"
              }
              className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-lg font-bold transition shadow-2xs ${
                batch.is_stale
                  ? "bg-stone-200 dark:bg-stone-800 text-stone-400 dark:text-stone-500 cursor-not-allowed border border-stone-300 dark:border-stone-700"
                  : "bg-emerald-600 hover:bg-emerald-500 text-white cursor-pointer"
              }`}
            >
              <PackageCheck className="w-3.5 h-3.5" />
              <span>Approve &amp; Promote</span>
            </button>
          </div>
        )}

        {batch.status === "completed" && (
          <button
            type="button"
            onClick={onOpenArchiveModal}
            disabled={actionLoading}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-stone-200 dark:border-stone-800 text-stone-600 dark:text-stone-400 font-medium hover:bg-stone-100 dark:hover:bg-stone-900 transition disabled:opacity-50"
          >
            <Archive className="w-3.5 h-3.5" />
            <span>Archive Batch</span>
          </button>
        )}
      </div>
    </div>
  );
}
